import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLocalStorage, getUser } from '../utils/func';
import config from '../utils/config';
import { subscribeSessionChanges } from '../hooks/useSessionIdentity';
import { directEntry, eligibleEntry, isPlainHome, skipVisit, saveProgress,
  pendingAccountOnboarding, onboardingReturn, resumeVisit, ONBOARDING_KEY, STEP_COUNT } from './state';
import { prepareOnboardingLaunch } from './launch';

const Context = createContext({ ready: true, eligible: false, scope: ONBOARDING_KEY,
  dismiss: () => {}, complete: () => {}, launchResolved: () => {} });
export const useOnboarding = () => useContext(Context);

export default function OnboardingProvider({ children, appScope }) {
  const location = useLocation();
  const [installedScope] = useState(() => appScope === undefined ? prepareOnboardingLaunch() : appScope);
  const [entryAllowed] = useState(() => {
    const allowed = isPlainHome(location) && !location.state && directEntry(document.referrer, Boolean(installedScope));
    const pending = installedScope && allowed && pendingAccountOnboarding(getUser()?.id);
    if (pending) resumeVisit(pending);
    return allowed;
  });
  const [ready, setReady] = useState(() => !(window.Capacitor?.isNativePlatform?.() === true
    || window.location.protocol === 'capacitor:'));
  const [dismissed, setDismissed] = useState(() => new Set());
  const [, sessionChanged] = useState(0);
  // Batch this with finishSignIn's onboarding marker and navigation. A forced
  // synchronous render between those steps can dismiss the new account course.
  useEffect(() => subscribeSessionChanges(() => sessionChanged(version => version + 1)), []);
  const user = getUser();
  const account = pendingAccountOnboarding(user?.id);
  const scope = account || installedScope || ONBOARDING_KEY;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const newAccountEntry = account && location.state?.onboardingAccount === String(user.id);
  const allowed = (entryAllowed || newAccountEntry) && isPlainHome(location)
    && (!location.state || newAccountEntry);
  const eligible = Boolean(allowed && !dismissed.has(scope) && eligibleEntry(location, document.referrer,
    Boolean(user?.id || getLocalStorage(config.token)), { scope, registration: newAccountEntry, installed: Boolean(installedScope) }));
  // Stable callbacks keep the native URL listener subscribed across sign-in.
  const dismiss = useCallback(() => {
    const current = scopeRef.current;
    skipVisit(current);
    setDismissed(previous => previous.has(current) ? previous : new Set([...previous, current]));
  }, []);
  const complete = useCallback(completedScope => {
    saveProgress(STEP_COUNT - 1, true, completedScope);
    // Account training also fulfils this installation's introduction.
    if (installedScope) saveProgress(STEP_COUNT - 1, true, installedScope);
    skipVisit(completedScope);
    setDismissed(previous => new Set([...previous, completedScope]));
  }, [installedScope]);
  const launchResolved = useCallback((unknown = false) => {
    if (unknown) dismiss();
    setReady(true);
  }, [dismiss]);
  useEffect(() => { if (!allowed) dismiss(); }, [allowed, scope, dismiss]);
  const returnTo = onboardingReturn(scope);
  const value = useMemo(() => ({ ready, eligible, scope, returnTo, dismiss, complete, launchResolved }),
    [ready, eligible, scope, returnTo, dismiss, complete, launchResolved]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
